package com.evoapps.evoshift.billing

import com.evoapps.evoshift.domain.billing.BillingCustomer
import com.evoapps.evoshift.domain.billing.BillingStore
import com.evoapps.evoshift.domain.billing.Entitlement
import com.evoapps.evoshift.domain.billing.PlanEntitlement
import com.evoapps.evoshift.domain.billing.PlanOffer
import com.evoapps.evoshift.domain.billing.Plans
import com.evoapps.evoshift.domain.util.AppClock
import kotlin.time.Instant

/**
 * Changing a running plan (SH-52). Each store SDK can only replace its own subscription: Google
 * Play through a replacement purchase, the App Store inside the subscription group, the web in
 * RevenueCat's customer portal. Buying on another platform would start a second subscription.
 */

/**
 * The plan the owner pays for in a store as this platform's SDK sees it (Plans.resolveStorePlan:
 * the highest tier, promotional grants left out): the subscription a tier change replaces.
 */
internal fun BillingCustomer.storePlan(now: Instant = AppClock.now()): PlanEntitlement? =
    Plans.resolveStorePlan(entitlements, now, subscriptionEnds)

/** Whether this plan was bought in a store other than [thisStore] and so must be changed there. */
internal fun Entitlement.isChangedElsewhereOn(thisStore: BillingStore): Boolean =
    store != thisStore && store.hasOwnManagement

/** How Google Play replaces the running subscription when the owner picks another package. */
internal enum class PlayReplacement {
    /** Nothing runs on Play, or the offer is the running product: an ordinary purchase. */
    NONE,
    /** More seats: the new plan starts now at its full price, unused time is credited as days. */
    MORE_SEATS_NOW,
    /** Fewer seats, or the same seats on another period: the switch waits for the renewal. */
    AT_RENEWAL
}

/**
 * The replacement for buying [offer] while the Play subscription [runningProductId] (as the
 * entitlement reports it: `subscription` or `subscription:basePlan`) is active.
 */
internal fun playReplacementFor(
    runningProductId: String?,
    offer: PlanOffer,
    runningSeats: Int = Plans.seatsFor(runningProductId, null)
): PlayReplacement = when {
    runningProductId == null || runningProductId == offer.productId -> PlayReplacement.NONE
    // Another base plan of the same subscription (monthly <-> yearly): Play only switches at the
    // renewal there (WITH_TIME_PRORATION is refused between options of one product).
    runningProductId.subscriptionId() == offer.productId.subscriptionId() -> PlayReplacement.AT_RENEWAL
    offer.seats > runningSeats -> PlayReplacement.MORE_SEATS_NOW
    else -> PlayReplacement.AT_RENEWAL
}

/** Play product ids are `subscription:basePlan` in offerings but only `subscription` on entitlements. */
internal fun String.subscriptionId(): String = substringBefore(':')
